import UtilService from "../../../services/UtilService";

/** Minimum account tier required to play a variant. */
export const PUZZLE_TIER = {
    REGISTERED: "registered",
    STANDARD: "standard",
};

const STAFF_ROLES = new Set(["ADMIN", "MANAGER", "CREATOR"]);

/** Sliding: Easy 4×4 → open; Medium/Hard 4×4 and all 6×6 → standard (sign-in + play credit). */
export function getSlidingGridTierRequirement(gridSize, difficultyLevel) {
    const gs = Number(gridSize);
    const lv = String(difficultyLevel ?? "").trim().toUpperCase();
    if (gs === 4 && lv === "EASY") return PUZZLE_TIER.REGISTERED;
    return PUZZLE_TIER.STANDARD;
}

/** Jigsaw: Easy (6×6) → open; Medium+ (9×9 and up) → standard (sign-in + play credit). */
export function getJigsawTileTierRequirement(tileSize) {
    const ts = Number(tileSize);
    if (ts === 6) return PUZZLE_TIER.REGISTERED;
    return PUZZLE_TIER.STANDARD;
}

export function buildPuzzleAccessContext(userInfo) {
    const cloudUser = userInfo?.cloudUser ?? null;
    const role = String(cloudUser?.userRole ?? "").toUpperCase();
    const isStaff = STAFF_ROLES.has(role);
    return {
        isAuthenticated: Boolean(cloudUser),
        userHasStandardPlan: isStaff || UtilService.isStandardPayPlanForUser(cloudUser),
        isStaff,
    };
}

export function buildPuzzleAccessContextFromPayPlan(userPayPlan, isAuthenticated = Boolean(userPayPlan)) {
    return {
        isAuthenticated,
        userHasStandardPlan: UtilService.isStandardPayPlan(userPayPlan),
        isStaff: false,
    };
}

export function canAccessPuzzleTier(tier, accessContext) {
    if (!accessContext) return false;
    if (accessContext.isStaff) return true;
    if (tier === PUZZLE_TIER.REGISTERED) return true;
    if (tier === PUZZLE_TIER.STANDARD) {
        return accessContext.isAuthenticated && accessContext.userHasStandardPlan;
    }
    return false;
}

/** @returns {"login"|"upgrade"|null} */
export function getPuzzleTierLockReason(tier, accessContext) {
    if (canAccessPuzzleTier(tier, accessContext)) return null;
    if (!accessContext?.isAuthenticated) return "login";
    return "upgrade";
}

export function canAccessSlidingGrid(gridSize, accessContext, difficultyLevel, options) {
    if (options?.isFreePuzzle === true) return true;
    const tier = getSlidingGridTierRequirement(gridSize, difficultyLevel);
    if (tier === PUZZLE_TIER.REGISTERED) return true;
    // Medium+ / 6×6: signed-in users may enter; board charges /home/credit once.
    if (accessContext?.isStaff) return true;
    return Boolean(accessContext?.isAuthenticated);
}

/** @returns {"login"|"upgrade"|null} */
export function getSlidingGridLockReason(gridSize, accessContext, difficultyLevel, options) {
    if (canAccessSlidingGrid(gridSize, accessContext, difficultyLevel, options)) return null;
    if (!accessContext?.isAuthenticated) return "login";
    return "upgrade";
}

/** True when this sliding variant is Premium-tier (credit-gated on play) — everything except Easy 4×4. */
export function isSlidingPremiumTier(gridSize, difficultyLevel) {
    return getSlidingGridTierRequirement(gridSize, difficultyLevel) === PUZZLE_TIER.STANDARD;
}

export function canAccessJigsawTile(tileSize, accessContext, options) {
    if (options?.isFreePuzzle === true) return true;
    const tier = getJigsawTileTierRequirement(tileSize);
    if (tier === PUZZLE_TIER.REGISTERED) return true;
    // Medium+ (9×9+): signed-in users may enter; board charges /home/credit once.
    if (accessContext?.isStaff) return true;
    return Boolean(accessContext?.isAuthenticated);
}

/** @returns {"login"|"upgrade"|null} */
export function getJigsawTileLockReason(tileSize, accessContext, options) {
    if (canAccessJigsawTile(tileSize, accessContext, options)) return null;
    if (!accessContext?.isAuthenticated) return "login";
    return "upgrade";
}

/** True when this jigsaw size is Premium-tier (credit-gated on play) — everything except Easy 6×6. */
export function isJigsawPremiumTier(tileSize) {
    return getJigsawTileTierRequirement(tileSize) === PUZZLE_TIER.STANDARD;
}

/** Normalize crossword / word-search / hidden-message `difficultyLevel`. */
export function normalizeWordPuzzleDifficulty(difficultyLevel) {
    return String(difficultyLevel ?? "").trim().toUpperCase();
}

/**
 * Word puzzles: Easy → open to everyone; Medium / Hard (and harder) → credit-gated
 * unless the puzzle itself is marked free (`options.isFreePuzzle`).
 */
export function isWordPuzzleOpenAccess(difficultyLevel) {
    return normalizeWordPuzzleDifficulty(difficultyLevel) === "EASY";
}

/** Medium / Hard+ charge 1 play credit once per puzzle (signed-in). */
export function isWordPuzzleCreditGated(difficultyLevel) {
    return !isWordPuzzleOpenAccess(difficultyLevel);
}

/**
 * Word puzzles (crossword / word search / hidden message):
 * Catalog-free puzzles → every difficulty, no lock or play credit.
 * Otherwise Easy → anyone; Medium/Hard → signed-in (credit-gated on play).
 */
export function canAccessWordPuzzle(difficultyLevel, accessContext, options) {
    if (options?.isFreePuzzle === true) return true;
    if (isWordPuzzleOpenAccess(difficultyLevel)) return true;
    if (accessContext?.isStaff) return true;
    return Boolean(accessContext?.isAuthenticated);
}

/** @returns {"login"|null} — upgrade is handled via credit paywall on the play page. */
export function getWordPuzzleLockReason(difficultyLevel, accessContext, options) {
    if (canAccessWordPuzzle(difficultyLevel, accessContext, options)) return null;
    return "login";
}

/** Template or story bundle marked free — every difficulty is open, no play-credit charge. */
export function isCatalogFreePuzzle(source) {
    if (!source || typeof source !== "object") return false;
    if (source.free === true) return true;
    if (source.template?.free === true) return true;
    return false;
}

/** @deprecated Prefer {@link isWordPuzzleCreditGated}. */
export function isWordPuzzlePremiumEdition(difficultyLevel) {
    return isWordPuzzleCreditGated(difficultyLevel);
}
